export interface SidebarBenefit {
  icon: string;
  title: string;
  description: string;
}

export interface SidebarContent {
  alliance: {
    badge1: string;
    title: string;
    description: string;
  };
  benefitsTitle: string;
  benefits: SidebarBenefit[];
  security: {
    title: string;
    description: string;
    certificate: string;
  };
  support: {
    title: string;
    linkText: string;
  };
}

export const SIDEBAR_CONTENT: SidebarContent = {
  alliance: {
    badge1: 'Follow My Car',
    title: 'Alianza Exclusiva',
    description: 'Accede a beneficios especiales por ser cliente {{insurer}}',
  },
  benefitsTitle: 'Beneficios incluidos con la APP',
  benefits: [
    {
      icon: 'shield-halved',
      title: 'Monitoreo 24/7',
      description: 'Central de control permanente',
    },
    {
      icon: 'tag',
      title: '3 primeros meses sin costo',
      description: 'En tu póliza {{insurer}}',
    },
    {
      icon: 'wrench',
      title: 'Instalación a domicilio',
      description: 'Sin costo de entrega',
    },
  ],
  security: {
    title: 'Datos Protegidos',
    description: 'Tu información está encriptada y protegida bajo estándares bancarios',
    certificate: 'Certificado SSL 256-bit',
  },
  support: {
    title: '¿Necesitas ayuda?',
    linkText: 'Contactar Soporte',
  },
};

export interface CountryCode {
  code: string;
  country: string;
  flag: string;
  phoneLength: number;
}

export interface Region {
  name: string;
  comunas: string[];
}

export const VEHICLE_BRANDS: string[] = [
  'Alfa Romeo',
  'Aston Martin',
  'Audi',
  'Austin',
  'Baic',
  'BMW',
  'Brillance',
  'Cadillac',
  'Changan',
  'Chery',
  'Chevrolet',
  'Chrysler',
  'Citroen',
  'Daewoo',
  'DFM',
  'DFSK',
  'Dodge',
  'FAW',
  'Ferrari',
  'Fiat',
  'Ford',
  'Foton',
  'Gac Gonow',
  'Geely',
  'Great Wall',
  'Hafei',
  'Haima',
  'Honda',
  'Hummer',
  'Hyundai',
  'Infinity',
  'Isuzu',
  'Iveco',
  'JAC',
  'Jaguar',
  'Jeep',
  'Kia',
  'Lada',
  'Lamborghini',
  'Land Rover',
  'Lexus',
  'Lifan',
  'Mahindra',
  'Maserati',
  'Maxus',
  'Mazda',
  'McLaren',
  'Mercedes Benz',
  'MG',
  'Mini',
  'Mitsubishi',
  'Nissan',
  'OldsMobile',
  'Opel',
  'Otro',
  'Peugeot',
  'Porsche',
  'Proton',
  'Renault',
  'Renault Samsung',
  'Rolls Royce',
  'Rover',
  'Saab',
  'Seat',
  'Skoda',
  'Ssangyong',
  'Subaru',
  'Suzuki',
  'Toyota',
  'Triumph',
  'Volkswagen',
  'Volvo',
  'Zotye',
];

export const COUNTRY_CODES: CountryCode[] = [
  { code: '+56', country: 'Chile', flag: '🇨🇱', phoneLength: 9 },
  { code: '+54', country: 'Argentina', flag: '🇦🇷', phoneLength: 10 },
  { code: '+51', country: 'Perú', flag: '🇵🇪', phoneLength: 9 },
  { code: '+55', country: 'Brasil', flag: '🇧🇷', phoneLength: 11 },
  { code: '+57', country: 'Colombia', flag: '🇨🇴', phoneLength: 10 },
];

// ─── Service Step Constants ───────────────────────────────────────────

import type { DeliveryMethod } from '@core/models/service-data.model';

export interface ServiceOption {
  id: DeliveryMethod;
  title: string;
  description: string;
  badge?: string;
  details: { type: 'time' | 'cost'; text: string }[];
}

export const SERVICE_OPTIONS: ServiceOption[] = [
  {
    id: 'retiro_sucursal',
    title: 'Retiro en Sucursal',
    description:
      'Retira tu dispositivo en una de nuestras sucursales y realiza la instalación por tu cuenta o en un taller de confianza.',
    details: [
      { type: 'time', text: 'Disponible inmediato' },
      { type: 'cost', text: 'Sin costo adicional' },
    ],
  },
  {
    id: 'envio_domicilio',
    title: 'Envío a Domicilio',
    description:
      'Recibe el dispositivo en tu domicilio y realiza la instalación por tu cuenta o en un taller de confianza.',
    details: [
      { type: 'time', text: '2-3 días hábiles' },
      { type: 'cost', text: 'Costo de envío según región' },
    ],
  },
  {
    id: 'instalacion_sucursal',
    title: 'Instalación en Sucursal',
    description:
      'Agenda una cita en nuestra sucursal y nuestros técnicos certificados instalarán el dispositivo en tu vehículo.',
    details: [
      { type: 'time', text: '45 minutos instalación' },
      { type: 'cost', text: '$8.000 costo servicio' },
    ],
  },
  {
    id: 'instalacion_domicilio',
    title: 'Instalación a Domicilio',
    description:
      'Un técnico certificado irá a tu domicilio para instalar el dispositivo GPS en tu vehículo.',
    badge: 'MÁXIMA COMODIDAD',
    details: [
      { type: 'time', text: '45 minutos instalación' },
      { type: 'cost', text: 'Costo según región' },
    ],
  },
];

/**
 * Métodos de entrega ocultos temporalmente del wizard.
 *
 * La entrada sigue viva en SERVICE_OPTIONS (fuente de verdad para resolver la
 * etiqueta/descripción del servicio en sidebar, resumen y confirmación, y para
 * validar estados restaurados). Solo se filtra de las tarjetas seleccionables
 * en step-servicio para que el cliente no pueda elegirla.
 *
 * 'instalacion_domicilio': Yellow aún no habilita instalación a domicilio.
 * Restaurar quitándolo de este set cuando la modalidad esté operativa. (SMFMC-794)
 */
export const HIDDEN_DELIVERY_METHODS: ReadonlySet<DeliveryMethod> = new Set([
  'instalacion_domicilio',
]);

/** Opciones de servicio seleccionables en el wizard (excluye las ocultas). */
export const SELECTABLE_SERVICE_OPTIONS: ServiceOption[] = SERVICE_OPTIONS.filter(
  (option) => !HIDDEN_DELIVERY_METHODS.has(option.id)
);

export interface AppFeature {
  title: string;
  description: string;
}

export const APP_FEATURES: AppFeature[] = [
  { title: 'Ubicación en tiempo real', description: 'Monitoreo 24/7 del vehículo' },
  { title: 'Alertas inteligentes', description: 'Movimiento, encendido y desconexión' },
  { title: 'Geocercas personalizadas', description: 'Notificaciones automáticas' },
  { title: 'Historial de recorridos', description: 'Visualización completa de rutas' },
  { title: 'Alertas de velocidad', description: 'Asistencia ante robo' },
  { title: 'Apoyo en emergencias', description: 'Canal de soporte prioritario' },
];

export const APP_ANNUAL_PRICE = 37490;
export const APP_REGULAR_PRICE = 49990;

export const PRIVACY_POLICY_URL = 'https://info.fmc.entelocean.io/politicas-de-privacidad';

export const WHATSAPP_SUPPORT_URL =
  'https://wa.me/56979661551?text=Hola%21%20Necesito%20ayuda%20con%20mis%20servicios%20de%20Follow%20My%20Car';

export const TERMS_AND_CONDITIONS_URL = 'https://info.fmc.entelocean.io/terminos-y-condiciones';

// ─── Branch Constants ─────────────────────────────────────────────────

import type { Branch, BranchType } from '@core/models/service-data.model';

export interface BranchTypeOption {
  id: BranchType;
  title: string;
  description: string;
}

export const BRANCH_TYPE_OPTIONS: BranchTypeOption[] = [
  {
    id: 'proveedor',
    title: 'Sucursal Proveedor',
    description: 'Red de sucursales propias',
  },
  {
    id: 'bci',
    title: 'Sucursal BCI Seguros',
    description: 'Red de oficinas BCI',
  },
  {
    id: 'fid',
    title: 'Sucursal FID',
    description: 'Red de oficinas FID',
  },
  {
    id: 'zenit',
    title: 'Sucursal Zenit',
    description: 'Red de oficinas Zenit',
  },
];

export const BRANCHES: Branch[] = [
  {
    id: 'proveedor-1',
    name: 'Las Condes',
    address: 'Napoleón',
    addressNumber: '3233',
    addressReference: null,
    schedule: 'Lunes a Viernes 9:00 - 18:00, Sábados 10:00 - 14:00',
    type: 'proveedor',
  },
  {
    id: 'bci-1',
    name: 'BCI Santiago Centro',
    address: 'Av. El Bosque Norte',
    addressNumber: '500',
    addressReference: null,
    schedule: 'Lunes a Viernes 9:00 - 17:00',
    type: 'bci',
  },
  {
    id: 'bci-2',
    name: 'BCI Providencia',
    address: 'Av. Providencia',
    addressNumber: '1800',
    addressReference: null,
    schedule: 'Lunes a Viernes 9:00 - 17:00',
    type: 'bci',
  },
  {
    id: 'fid-1',
    name: 'FID Santiago Centro',
    address: 'Av. El Bosque Sur',
    addressNumber: '500',
    addressReference: null,
    schedule: 'Lunes a Viernes 9:00 - 17:00',
    type: 'fid',
  },
  {
    id: 'fid-2',
    name: 'FID Providencia',
    address: 'Av. Nueva Providencia',
    addressNumber: '1800',
    addressReference: null,
    schedule: 'Lunes a Viernes 9:00 - 17:00',
    type: 'fid',
  },
  {
    id: 'zenit-1',
    name: 'Zenit Santiago Centro',
    address: 'Av. El Bosque Norte',
    addressNumber: '700',
    addressReference: null,
    schedule: 'Lunes a Viernes 9:00 - 17:00',
    type: 'zenit',
  },
  {
    id: 'zenit-2',
    name: 'Zenit Providencia',
    address: 'Av. Providencia',
    addressNumber: '900',
    addressReference: null,
    schedule: 'Lunes a Viernes 9:00 - 17:00',
    type: 'zenit',
  },
];

// ─── Cal.com Constants ──────────────────────────────────────────────

export const CAL_EVENT_TYPE_ID_SUCURSAL = 4494706;

export const CAL_EVENT_TYPE_ID_DOMICILIO: Record<string, number> = {
  antofagasta: 4723829,
  'la araucanía': 4723860,
  maule: 4723865,
  metropolitana: 4723873,
  valparaíso: 4723875,
};

// ─── Verification Constants ──────────────────────────────────────────

/**
 * Verification code expiration time in seconds (5 minutes)
 */
export const VERIFICATION_CODE_EXPIRATION_SECONDS = 300;

/**
 * Cooldown time in seconds before allowing resend of verification code (1 minute)
 */
export const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Maximum number of failed verification attempts before requiring a new code
 */
export const MAX_VERIFICATION_ATTEMPTS = 5;

/**
 * Base cooldown in seconds between failed verification attempts.
 * Actual cooldown = base * attemptNumber (progressive: 3s, 6s, 9s, 12s...)
 */
export const ATTEMPT_COOLDOWN_BASE_SECONDS = 3;

/**
 * Comunas in the Metropolitana region that have coverage for installation at home.
 */
export const METROPOLITANA_COMUNAS_INSTALACION_DOMICILIO: string[] = [
  'Cerrillos',
  'Cerro Navia',
  'Conchalí',
  'El Bosque',
  'Estación Central',
  'Huechuraba',
  'Independencia',
  'La Cisterna',
  'La Florida',
  'La Granja',
  'La Reina',
  'Las Condes',
  'Lo Barnechea',
  'Lo Espejo',
  'Lo Prado',
  'Macul',
  'Maipú',
  'Ñuñoa',
  'Pedro Aguirre Cerda',
  'Peñalolén',
  'Providencia',
  'Pudahuel',
  'Quilicura',
  'Quinta Normal',
  'Recoleta',
  'Renca',
  'San Joaquín',
  'San Miguel',
  'San Ramón',
  'Santiago',
  'Vitacura',
];
